/**
 * The signed-out half of mock mode.
 *
 * `VITE_AUTH_MODE=mock` exists so the SPA runs with no Entra tenant behind it.
 * It used to start already signed in, which left two things unreachable without
 * a real tenant: the sign-in screen of §12.1, and the deep-link round trip of
 * FR-1.4 — the one a shared book link depends on.
 *
 * What is modelled here is the only part of the redirect flow the SPA can
 * observe: the browser leaves the app, comes back at the redirect URI, and MSAL
 * then puts it back on the URL the sign-in started from (`navigateToLoginRequestUrl`
 * in `msal.ts`). Both hops are full document loads, so everything the app held
 * in memory — the router's `location.state` among it — is gone by the time it
 * runs again. That is precisely the condition `returnTo.ts` exists to survive,
 * and modelling it is what makes it testable.
 *
 * Reached only when `env.authMode === 'mock'`. A production bundle is never
 * built that way, and `deploy-web.yml` refuses one that is.
 */

import { LANGUAGES, type Role, type UiLanguage } from '@/lib/types'

const SIGNED_OUT = 'itpu.library.mock.signedOut'
const ROLES = 'itpu.library.mock.roles'
const LANGUAGE = 'itpu.library.mock.language'
const LOGIN_REQUEST = 'itpu.library.mock.loginRequest'

/** The redirect URI of the real registration is the origin (see `msal.ts`). */
const REDIRECT_URI = '/'

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Site data blocked. Mock mode then behaves as it always did — signed in.
  }
}

/** Mock mode signs in by default; a sign-out is what makes it stick. */
export function isMockSignedOut(): boolean {
  return read(SIGNED_OUT) === '1'
}

/** Leaves for the "identity provider", remembering where to come back to. */
export function beginMockSignIn(): void {
  write(LOGIN_REQUEST, `${window.location.pathname}${window.location.search}`)
  write(SIGNED_OUT, null)
  window.location.assign(REDIRECT_URI)
}

/**
 * Read once and forgotten, so a later reload of the redirect URI does not
 * bounce the visitor somewhere they did not ask for.
 */
export function takeMockLoginRequest(): string | null {
  const path = read(LOGIN_REQUEST)
  write(LOGIN_REQUEST, null)
  return path !== null && path.startsWith('/') && !path.startsWith('//') ? path : null
}

export function forgetMockSession(): void {
  write(LOGIN_REQUEST, null)
  write(SIGNED_OUT, '1')
}

/**
 * Which roles the mock account carries.
 *
 * The default is every role, so the whole UI is reachable in development. A
 * comma-separated override answers the question a developer otherwise cannot
 * ask without a second Entra account: what does this screen look like to a
 * student? It moves nothing — the API decides what a caller may do on every
 * request (§4.3), and this account has no API behind it at all.
 */
export function mockRoles(fallback: readonly Role[]): Role[] {
  const raw = read(ROLES)
  if (raw === null) return [...fallback]
  const known: Role[] = ['user', 'teacher', 'admin']
  const chosen = raw
    .split(',')
    .map((value) => value.trim())
    .filter((value): value is Role => known.includes(value as Role))
  // 'user' is not a grant, it is what being signed in means (§3.1).
  return chosen.length > 0 ? [...new Set<Role>(['user', ...chosen])] : [...fallback]
}

/**
 * Stands in for the language saved on the profile (FR-7.4).
 *
 * Without it mock mode contradicts the product: the profile's language wins
 * over the local one by FR-7.3, so a hardcoded 'en' would snap the interface
 * back to English on every reload however many times the switcher was used.
 */
export function rememberMockLanguage(language: UiLanguage): void {
  write(LANGUAGE, language)
}

export function mockLanguage(fallback: UiLanguage): UiLanguage {
  const stored = read(LANGUAGE)
  return LANGUAGES.includes(stored as UiLanguage) ? (stored as UiLanguage) : fallback
}
